import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Save as SaveIcon,
  HelpOutline as HelpIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  Person as UserIcon
} from '@mui/icons-material';
import useSection from '../../hooks/useSection';
import useNotification from '../../hooks/useNotification';
import useAuth from '../../hooks/useAuth';

/**
 * Component to manage section activation based on user roles
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onUpdate - Callback when sections are updated
 * @returns {React.ReactElement} Component
 */
const SectionRoleManager = ({ onUpdate }) => {
  const { sections, isLoadingSections, sectionsError, updateSectionRoles, refreshSections } = useSection();
  const { showNotification } = useNotification();
  const { hasPermission, user } = useAuth();
  
  // State for role mappings
  const [roleMappings, setRoleMappings] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAllSections, setShowAllSections] = useState(false);
  
  // Available roles in the system
  const availableRoles = [
    { id: 'admin', name: 'Administrator', icon: <AdminIcon fontSize="small" /> },
    { id: 'manager', name: 'Manager', icon: <ManagerIcon fontSize="small" /> },
    { id: 'user', name: 'Standard User', icon: <UserIcon fontSize="small" /> }
  ];
  
  // Initialize role mappings from sections data
  useEffect(() => {
    if (sections && sections.length > 0) {
      const newRoleMappings = {};
      
      sections.forEach(section => {
        newRoleMappings[section._id] = {
          isActive: section.isActive || false,
          roles: section.requiredRoles || []
        };
      });
      
      setRoleMappings(newRoleMappings);
    }
  }, [sections]);
  
  // Handle toggling a role for a section
  const handleRoleToggle = (sectionId, roleId) => {
    setRoleMappings(prevState => {
      const updatedMapping = { ...prevState };
      const roles = [...(updatedMapping[sectionId]?.roles || [])];
      
      // Toggle the role (add if not present, remove if present)
      const roleIndex = roles.indexOf(roleId);
      if (roleIndex > -1) {
        roles.splice(roleIndex, 1);
      } else {
        roles.push(roleId);
      }
      
      // Update the mapping
      updatedMapping[sectionId] = {
        ...updatedMapping[sectionId],
        roles
      };
      
      return updatedMapping;
    });
  };
  
  // Handle toggling section activation
  const handleSectionActiveToggle = (sectionId) => {
    setRoleMappings(prevState => {
      const updatedMapping = { ...prevState };
      const currentActive = updatedMapping[sectionId]?.isActive || false;
      
      updatedMapping[sectionId] = {
        ...updatedMapping[sectionId],
        isActive: !currentActive
      };
      
      return updatedMapping;
    });
  };
  
  // Save role mappings to sections
  const handleSaveRoleMappings = async () => {
    try {
      setLoading(true);
      
      // Convert mappings to the format expected by the API
      const updatedSections = Object.entries(roleMappings).map(([sectionId, mapping]) => ({
        sectionId,
        isActive: mapping.isActive,
        requiredRoles: mapping.roles
      }));
      
      // Call API to update section roles
      await updateSectionRoles(updatedSections);
      
      // Refresh sections data
      await refreshSections();
      
      showNotification({
        message: 'Section role settings saved successfully',
        type: 'success'
      });
      
      // Call the onUpdate callback if provided
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving section role mappings:', error);
      showNotification({
        message: `Failed to save role settings: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Check if the current user has permission to edit section roles
  const canEditRoles = hasPermission('sections', 'update') || user?.role === 'admin';
  
  if (isLoadingSections) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (sectionsError) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Error loading sections: {sectionsError.message}
      </Alert>
    );
  }
  
  const filteredSections = showAllSections 
    ? sections 
    : sections.filter(section => section.isActive);
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          Section Role Management
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showAllSections}
                onChange={() => setShowAllSections(!showAllSections)}
                color="primary"
              />
            }
            label="Show all sections"
          />
          
          {canEditRoles && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveRoleMappings}
              disabled={loading}
              sx={{ 
                bgcolor: 'var(--color-blue)',
                '&:hover': {
                  bgcolor: 'var(--color-blue-dark)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Settings'}
            </Button>
          )}
        </Box>
      </Box>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Manage which user roles have access to each section of the project creation workflow.
        When a section is activated for a role, users with that role will see the section and its tasks.
      </Alert>
      
      <TableContainer>
        <Table sx={{ minWidth: 650 }} size="medium">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Section</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Active</TableCell>
              {availableRoles.map(role => (
                <TableCell key={role.id} align="center" sx={{ fontWeight: 'bold' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {role.icon}
                    <Typography sx={{ ml: 1 }}>{role.name}</Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSections.length > 0 ? (
              filteredSections.map(section => (
                <TableRow key={section._id} hover>
                  <TableCell component="th" scope="row">
                    <Typography fontWeight={500}>{section.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {section.description}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                        label={`${section.tasks?.length || 0} tasks`} 
                        size="small" 
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={roleMappings[section._id]?.isActive || false}
                      onChange={() => handleSectionActiveToggle(section._id)}
                      disabled={!canEditRoles}
                      color="primary"
                    />
                  </TableCell>
                  {availableRoles.map(role => (
                    <TableCell key={role.id} align="center">
                      <Checkbox
                        checked={roleMappings[section._id]?.roles.includes(role.id) || false}
                        onChange={() => handleRoleToggle(section._id, role.id)}
                        disabled={!canEditRoles || !roleMappings[section._id]?.isActive}
                        color="primary"
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3 + availableRoles.length} align="center">
                  <Typography color="text.secondary">
                    {showAllSections ? 'No sections found' : 'No active sections found'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {!canEditRoles && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          You don't have permission to edit section role settings. Contact an administrator for access.
        </Alert>
      )}
    </Paper>
  );
};

export default SectionRoleManager;